import React, {
  createContext, useCallback, useContext, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { colors, webColors, fonts } from '../theme';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

/**
 * Full-screen navigation transition — a dark "aperture" that opens from the
 * clicked point, holds a small custom loader while the destination route's
 * code-split chunk preloads, then contracts to reveal the destination.
 *
 * Lives at the App level (outside <Routes>) so it survives the route swap
 * that would otherwise unmount it mid-animation (the original bug behind
 * the solid-color flash — the old curtain lived inside the page being
 * replaced, so its exit animation never had a chance to run).
 */

const OVERLAY_BG = '#07070B';
const ease = [0.65, 0, 0.2, 1];

const palette = {
  coaching: colors,
  web: webColors,
};

const TransitionCtx = createContext(null);

export function usePageTransition() {
  const ctx = useContext(TransitionCtx);
  if (!ctx) throw new Error('usePageTransition must be used within PageTransitionProvider');
  return ctx;
}

const circle = (r, x, y) => `circle(${r}px at ${x}px ${y}px)`;
const delay = (ms) => new Promise((res) => setTimeout(res, ms));
const raf = () => new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 200;
  pointer-events: none;
  background: ${OVERLAY_BG};
`;

const Wash = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: ${({ $mode }) =>
    `radial-gradient(circle 620px at var(--ox) var(--oy), ${palette[$mode].accentGlow} 0%, transparent 65%)`};
`;

const Streak = styled(motion.div)`
  position: absolute;
  top: var(--oy);
  left: calc(var(--ox) - 70vw);
  width: 140vw;
  height: 1px;
  background: ${({ $mode }) =>
    `linear-gradient(90deg, transparent, ${palette[$mode].accentLight}, transparent)`};
`;

const LoaderWrap = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const spin = 'spin 1.1s linear infinite';

const Ring = styled.svg`
  width: 56px;
  height: 56px;
  animation: ${({ $spin }) => ($spin ? spin : 'none')};

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Glyph = styled.span`
  position: absolute;
  font-family: ${fonts.mono};
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  color: ${({ $mode }) => palette[$mode].accentLight};
`;

function Loader({ mode, reduced }) {
  const accent = palette[mode].accentLight;
  return (
    <LoaderWrap
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.85 }}
      transition={{ duration: 0.25, ease }}
    >
      <Ring viewBox="0 0 56 56" $spin={!reduced}>
        <circle cx="28" cy="28" r="24" fill="none" stroke={colors.border} strokeWidth="1.5" />
        <circle
          cx="28" cy="28" r="24" fill="none" stroke={accent} strokeWidth="1.5"
          strokeLinecap="round" strokeDasharray="70 150"
        />
      </Ring>
      {mode === 'web' && <Glyph $mode={mode}>{'</>'}</Glyph>}
    </LoaderWrap>
  );
}

export function PageTransitionProvider({ children, loaders }) {
  const navigate = useNavigate();
  const reduced = usePrefersReducedMotion();
  const [state, setState] = useState({ active: false, mode: null, phase: 'enter', origin: { x: 0, y: 0 }, radius: 0 });
  const busy = useRef(false);

  const go = useCallback(async (mode, evt, path) => {
    if (busy.current) return;
    busy.current = true;

    const x = evt?.clientX ?? window.innerWidth / 2;
    const y = evt?.clientY ?? window.innerHeight / 2;
    // Radius that guarantees full coverage regardless of where on screen the
    // origin point sits — distance to the farthest viewport corner.
    const corners = [
      [0, 0], [window.innerWidth, 0], [0, window.innerHeight], [window.innerWidth, window.innerHeight],
    ];
    const radius = Math.max(...corners.map(([cx, cy]) => Math.hypot(cx - x, cy - y))) + 24;

    setState({ active: true, mode, phase: 'enter', origin: { x, y }, radius });

    const minHold = reduced ? 260 : 520;
    const bufferMs = reduced ? 40 : 70;
    const exitMs = reduced ? 200 : 420;

    const preload = loaders[mode] ? loaders[mode]() : Promise.resolve();
    await Promise.all([preload, delay(minHold)]);

    navigate(path);
    await raf();
    await delay(bufferMs);

    setState((s) => ({ ...s, phase: 'exit' }));
    await delay(exitMs);

    setState((s) => ({ ...s, active: false }));
    busy.current = false;
  }, [navigate, reduced, loaders]);

  const value = { go };
  const { active, mode, phase, origin, radius } = state;
  const enterDuration = reduced ? 0.16 : 0.46;
  const exitDuration = reduced ? 0.2 : 0.42;

  return (
    <TransitionCtx.Provider value={value}>
      {children}

      <AnimatePresence>
        {active && (
          <Overlay
            style={{ '--ox': `${origin.x}px`, '--oy': `${origin.y}px` }}
            initial={reduced ? { opacity: 0 } : { clipPath: circle(0, origin.x, origin.y) }}
            animate={
              reduced
                ? { opacity: phase === 'exit' ? 0 : 1 }
                : { clipPath: circle(phase === 'exit' ? 0 : radius, origin.x, origin.y) }
            }
            exit={reduced ? { opacity: 0 } : { clipPath: circle(0, origin.x, origin.y) }}
            transition={{ duration: phase === 'exit' ? exitDuration : enterDuration, ease }}
          >
            {!reduced && phase === 'enter' && (
              <Wash
                $mode={mode}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.7, 0.3] }}
                transition={{ duration: 0.5, ease }}
              />
            )}
            {!reduced && phase === 'enter' && [0, 1].map((i) => (
              <Streak
                key={i}
                $mode={mode}
                style={{ transform: `rotate(${mode === 'web' ? -6 : 4}deg) translateY(${i * 18 - 9}px)` }}
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: [0, 0.5, 0], x: 40 }}
                transition={{ duration: 0.5, delay: i * 0.06, ease }}
              />
            ))}
            <AnimatePresence>
              {phase === 'enter' && <Loader key="loader" mode={mode} reduced={reduced} />}
            </AnimatePresence>
          </Overlay>
        )}
      </AnimatePresence>
    </TransitionCtx.Provider>
  );
}

export function useNavigateWithTransition(path) {
  const { go } = usePageTransition();
  return useCallback((mode, evt) => go(mode, evt, path ?? `/${mode}`), [go, path]);
}



